<template>
  <v-container class="pa-0">
    <v-layout row wrap class="pa-3">
      <v-flex md3 sm6 xs12>
        <v-app-bar color="transparent" class="elevation-0 pa-0 page-header-toolbar">
          <page-header-title :title="$t('coursetemplates.title')"></page-header-title>
        </v-app-bar>

        <v-card class="mr-3">
          <v-card-text>
            <v-flex xs12>
              <v-select
                v-model="selected_ctg"
                :items="coursetypegroups"
                item-text="fullname"
                item-value="id"
                :label="$t('calendar.filter_ctgroup')"
                @change="filter"
                clearable
                multiple
              >
              </v-select>
            </v-flex>
          </v-card-text>

          <v-list id="draggable-items">
            <v-list-group no-action v-for="(ct, i) in filteredcoursetypes" :key="i">
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>{{ ct.fullname }}</v-list-item-title>
                </v-list-item-content>
              </template>

              <v-list-item v-for="(template, t) in ct.coursetemplates" :key="t" class="pl-10">
                <v-list-item-title class="fc-event-ct" :id="ct.id + ',' + template.id">{{ template.name }}</v-list-item-title>
              </v-list-item>
            </v-list-group>
          </v-list>
        </v-card>
      </v-flex>
      <v-flex md9 sm6 xs12>
        <!-- Calendar -->
        <v-app-bar color="transparent" class="elevation-0 pa-0 page-header-toolbar">
          <page-header-title :title="$t('calendar.title')"></page-header-title>

          <v-spacer></v-spacer>

          <v-form ref="form" @submit.prevent="filter" class="mt-8 d-sm-flex">
            <v-container grid-list-md>
              <v-layout row wrap>
                <v-flex xs12>
                  <v-autocomplete
                    v-model="selectedInstructor"
                    :items="instructors"
                    item-text="fullname"
                    item-value="id"
                    :label="$t('calendar.instructor')"
                    autocomplete="new-password"
                    clearable
                    multiple
                  ></v-autocomplete>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>

          <v-btn color="info" @click.stop="showSelectEventForm(true)" class="darken-2">
            <v-icon>add</v-icon>
            <span class="cstm-v-btn-text d-none d-sm-inline">{{ $t("global.add") }}</span>
          </v-btn>
        </v-app-bar>

        <v-card>
          <v-divider></v-divider>
          <v-container>
            <FullCalendar
              ref="fullCalendar"
              class="fullCalendar"
              defaultView="dayGridMonth"
              height="parent"
              :selectable="true"
              timeZone="local"
              :header="calendarHeader"
              :plugins="calendarPlugins"
              :weekends="calendarWeekends"
              :events="calendarEvents"
              :droppable="true"
              :locales="locales"
              :locale="$locale_bcp47($getSetting('locale'))"
              :custom-buttons="customButtons"
              @select="handleDatesSelected"
              @eventClick="handleEventClick"
              @eventReceive="handleEventReceive"
            />

            <v-dialog v-model="showDialogSelectEvent" max-width="290">
              <div class="form-modal pa-4">
                <v-card>
                  <v-card-title class="justify-center mb-3">
                    <span class="white--text">Select Event Type</span>
                  </v-card-title>
                  <v-card-text>
                    <v-btn
                      color="info"
                      width="100%"
                      class="mb-3 darken-2"
                      v-for="(item, i) in modules"
                      :key="i"
                      @click="showForm(null, item.key)"
                    >
                      {{ item.text }}
                    </v-btn>
                  </v-card-text>
                </v-card>
              </div>
            </v-dialog>

            <v-dialog v-model="showDialogForm" persistent max-width="960px">
              <div class="form-modal pa-4">
                <component
                  :is="currentLoadedForm.form"
                  :id="currentLoadedForm.id"
                  :modal="true"
                  from_module="calendar"
                  :start_date="currentStartDate"
                  :start_time="currentStartTime"
                  :end_date="currentEndDate"
                  :end_time="currentEndTime"
                  :coursetype_id="currentCourseTypeId"
                  :coursetemplate_id="currentCourseTemplateId"
                  @return_modal_form="return_modal_form"
                  @return_modal_cancel_form="return_modal_cancel_form"
                ></component>
              </div>
            </v-dialog>

            <v-dialog v-model="showDialogView" persistent max-width="960px">
              <div class="form-modal pa-4">
                <component
                  :is="currentLoadedView.view"
                  :id="currentLoadedView.id"
                  :modal="true"
                  @return_modal_view="return_modal_view"
                  @return_modal_cancel_view="return_modal_cancel_view"
                ></component>
              </div>
            </v-dialog>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import FullCalendar from "@fullcalendar/vue"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin, { Draggable } from "@fullcalendar/interaction"
import listPlugin from "@fullcalendar/list"

import enLocale from "@fullcalendar/core/locales/en-gb"
import noLocale from "@fullcalendar/core/locales/nb"

import "@fullcalendar/core/main.css"
import "@fullcalendar/daygrid/main.css"
import "@fullcalendar/timegrid/main.css"
import "@fullcalendar/list/main.css"

export default {
  name: "calendarView",

  components: {
    FullCalendar,
    "task-form": () => import("../../tasks/components/Form"),
    "drivingtest-form": () => import("../../drivingtests/components/Form"),
    "course-form": () => import("../../courses/components/Form"),
    "task-view": () => import("../../tasks/components/View"),
    "drivingtest-view": () => import("../../drivingtests/components/View"),
    "course-view": () => import("../../courses/components/View")
  },

  data() {
    return {
      show_filter: false,

      locales: [enLocale, noLocale],

      instructors: [],
      selectedInstructor: "",

      coursetemplates: [],
      coursetypes: [],
      filteredcoursetypes: [],

      selected_ctg: [],
      coursetypegroups: [],

      calendarApi: null,
      calendarHeader: {
        left: "today prev,next",
        center: "title",
        right: "dayGridMonth,dayGridWeek,timeGridDay,listWeek"
      },
      customButtons: {},
      calendarPlugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
      calendarEvents: [],
      calendarWeekends: true,

      currentFormId: "",
      currentForm: "",
      currentStartDate: null,
      currentStartTime: null,
      currentEndDate: null,
      currentEndTime: null,
      currentCourseTypeId: null,
      currentCourseTemplateId: null,
      showDialogForm: false,

      currentViewId: "",
      currentView: "",
      showDialogView: false,

      showDialogSelectEvent: false,

      showTask: true,
      showDrivingTest: true,
      showCourse: true,

      eventTaskColor: "light-blue",
      eventCourseColor: "cyan",
      eventDrivingTestColor: "teal",

      modules: [
        { key: "task", text: this.$t("calendar.new_task") },
        {
          key: "drivingtest",
          text: this.$t("calendar.new_driving_test")
        },
        { key: "course", text: this.$t("calendar.new_course") }
      ]
    }
  },

  created() {
    axios
      .post(this.API_URL + "request/ddlists", [
        { key: "coursetypegroups", module: "coursetypegroups", query: "show_all=true" },
        { key: "coursetypes", module: "coursetypes", query: "show_all=true" },
        { key: "instructors", module: "users", query: "show_all=true&role=Course Instructor" }
      ])
      .then((res) => {
        this.coursetypes = res.data.coursetypes
        this.filteredcoursetypes = JSON.parse(JSON.stringify(this.coursetypes))
        this.coursetypegroups = res.data.coursetypegroups
        this.instructors.push({ id: "not-assigned", fullname: this.$t("calendar.not_assigned") })
        res.data.instructors.forEach((item, index) => {
          this.instructors.push(item)
        })

        let ctg_ids = this.$getSetting("calendar_selected_coursetypegroup")
        if (ctg_ids) {
          let selected = ctg_ids.split(",")
          selected.forEach((item) => {
            this.selected_ctg.push(Number(item))
          })
          this.filter()
        }
      })
  },

  mounted() {
    this.calendarApi = this.$refs.fullCalendar.getApi()
    this.setupDraggable()
    this.customButtons = {
      prev: {
        click: () => {
          this.calendarApi.prev()
          this.getEvents()
        }
      },
      next: {
        click: () => {
          this.calendarApi.next()
          this.getEvents()
        }
      },
      today: {
        text:  this.btnTodayText,
        click: () => {
          this.calendarApi.next()
          this.getEvents()
        }
      }
    }
  },

  computed: {
    btnTodayText() {
      return this.$getSetting('locale') == "en" ? "Today" : "I dag"
    },
    currentLoadedForm: function () {
      let form,
        id,
        start_date,
        start_time,
        end_date,
        end_time,
        coursetype_id,
        coursetemplate_id = null

      if (this.currentForm) {
        this.showDialogForm = true

        id = this.currentFormId
        form = this.currentForm
        start_date = this.currentStartDate
        start_time = this.currentStartTime
        end_date = this.currentEndDate
        end_time = this.currentEndTime
        coursetype_id = this.currentCourseTypeId
        coursetemplate_id = this.currentCourseTemplateId
      }

      return {
        id: id,
        form: form,
        start_date: start_date,
        start_time: start_time,
        end_date: end_date,
        end_time: end_time,
        coursetype_id: coursetype_id,
        coursetemplate_id: coursetemplate_id
      }
    },
    currentLoadedView: function () {
      let view,
        id = null

      if (this.currentView) {
        this.showDialogView = true

        id = this.currentViewId
        view = this.currentView
      }

      return {
        id: id,
        view: view
      }
    },
    currentCalendarView: function () {
      return this.calendarApi ? this.calendarApi.view.type : null
    }
  },

  watch: {
    selectedInstructor: {
      handler() {
        this.refreshLoadedEvents("task")
        this.refreshLoadedEvents("course")
        this.refreshLoadedEvents("drivingtest")
      }
    },
    currentCalendarView: {
      handler: function () {
        this.getEvents()
      }
    }
  },

  methods: {
    getEvents() {
      this.calendarEvents = []

      let startDate = this.moment(this.calendarApi.view.currentStart).format(this.MYSQL_DATE_FORMAT)
      let endDate = this.moment(this.calendarApi.view.currentEnd).format(this.MYSQL_DATE_FORMAT)

      let lists = [
        {
          key: "courses",
          module: "courses",
          query: "show_all=true&resourcetype=course-basic-list&query_start_date=between-" + startDate + "-to-" + endDate
        },
        { key: "tasks", module: "tasks", query: "show_all=true&query_start_date=between-" + startDate + "-to-" + endDate },
        {
          key: "drivingtests",
          module: "drivingtests",
          query: "show_all=true&query_date=between-" + startDate + "-to-" + endDate
        }
      ]

      axios.post(this.API_URL + "request/ddlists", lists).then((res) => {
        // ADD EVENT - TASK
        res.data.tasks.forEach((item, index) => {
          this.addEvent(item, "task")
        })

        // ADD EVENT - COURSE
        res.data.courses.forEach((item, index) => {
          this.addEvent(item, "course")
        })

        // ADD EVENT - DRIVING TEST
        res.data.drivingtests.forEach((item, index) => {
          this.addEvent(item, "drivingtest")
        })
      })
    },
    filter() {
      let vm = this

      if (this.selected_ctg.length > 0) {
        this.filteredcoursetypes = this.coursetypes.filter((item) => vm.selected_ctg.includes(item.coursetypegroup_id))
      } else {
        this.filteredcoursetypes = this.coursetypes
      }

      this.$updateSettingDB("calendar_selected_coursetypegroup", vm.selected_ctg.join(","))
    },
    refreshLoadedEvents(type) {
      let events = this.calendarEvents.filter((item) => item.extendedProps.type == type)

      let show = null

      switch (type) {
        case "task":
          show = this.showTask
          break
        case "course":
          show = this.showCourse
          break
        case "drivingtest":
          show = this.showDrivingTest
          break
      }

      if (this.selectedInstructor.length > 0) {
        events.filter((event) => {
          if (!this.selectedInstructor.includes(event.extendedProps.instructor_id)) {
            event.classNames.push("event-hidden")
          } else {
            event.classNames = event.classNames.filter((item) => item != "event-hidden")
          }
        })
      } else {
        events.forEach((event, index) => {
          event.classNames = event.classNames.filter((item) => item != "event-hidden")
        })
      }

      events.forEach((event, index) => {
        if (show) {
          if (!event.classNames.includes("event-hidden")) {
            event.classNames = event.classNames.filter((item) => item != "event-hidden")
          }
        } else {
          event.classNames.push("event-hidden")
        }
      })
    },
    addEvent(item, type) {
      let obj = {}
      let title = ""

      switch (type) {
        case "task":
          title = "[T] " + item.title

          switch (this.currentCalendarView) {
            case "dayGridMonth":
              title =
                "[T] " +
                item.title +
                " " +
                item.start_time +
                " - " +
                item.end_time +
                (item.instructor.id ? " - " + item.instructor.initials : "")
              break
            case "dayGridWeek":
              title =
                "[T] " +
                item.title +
                " " +
                item.start_time +
                " - " +
                item.end_time +
                (item.instructor.id ? " - " + item.instructor.initials : "")
              break
            case "timeGridDay":
              title = "[T] " + item.title + (item.instructor.id ? " - " + item.instructor.initials : "")
              break
            case "listWeek":
              title = "[T] " + item.title + (item.instructor.id ? " - " + item.instructor.initials : "")
              break
          }

          obj = {
            id: type + "-" + item.id,
            title: title,
            start: item.start_date + " " + item.start_time,
            end: item.end_date + " " + item.end_time,
            extendedProps: {
              id: item.id,
              instructor_id: item.instructor_id ? item.instructor_id : "not-assigned",
              type: "task"
            },
            classNames: ["event-type", "event-type-task", this.eventTaskColor, "lighten-1"]
          }
          break
        case "course":
          obj = {
            id: type + "-" + item.id,
            title:
              "[C] " +
              item.name +
              (item.instructor.id ? " - " + item.instructor.initials : "") +
              (" - " + item.num_enrolled + " of " + item.num_places),
            start: item.start_date + " " + item.start_time,
            end: item.end_date + " " + item.end_time,
            extendedProps: {
              id: item.id,
              instructor_id: item.instructor_id ? item.instructor_id : "not-assigned",
              type: "course"
            },
            classNames: ["event-type", "event-type-course", this.eventCourseColor, "lighten-1"]
          }
          break
        case "drivingtest":
          title = "[D] " + item.participant.full_name

          switch (this.currentCalendarView) {
            case "dayGridMonth":
              title = "[D] " + item.participant.full_name + (item.class_id ? " - " + item.class.name : "")
              break
            case "dayGridWeek":
              title =
                "[D] " +
                item.participant.full_name +
                (item.class_id ? " - " + item.class.name : "") +
                (item.instructor.id ? " - " + item.instructor.initials : "")
              break
            case "timeGridDay":
              title =
                "[D] " +
                item.participant.full_name +
                (item.class_id ? " - " + item.class.name : "") +
                (item.instructor.id ? " - " + item.instructor.initials : "")
              break
            case "listWeek":
              title =
                "[D] " +
                item.participant.full_name +
                (item.class_id ? " - " + item.class.name : "") +
                (item.instructor.id ? " - " + item.instructor.initials : "")
              break
          }

          obj = {
            id: type + "-" + item.id,
            title: title,
            date: item.date,
            extendedProps: {
              id: item.id,
              instructor_id: item.instructor_id ? item.instructor_id : "not-assigned",
              type: "drivingtest"
            },
            classNames: ["event-type", "event-type-drivingtest", this.eventDrivingTestColor, "lighten-1"]
          }
          break
      }

      if (obj) {
        this.calendarEvents.push(obj)
      }
    },
    updateEvent(item, type) {
      let event = this.calendarEvents.find((event) => event.extendedProps.id == item.id && event.extendedProps.type == type)

      this.calendarEvents = this.calendarEvents.filter(function (e) {
        return e.id !== event.id
      })

      this.addEvent(item, type)
    },
    showForm(id, type) {
      this.showDialogSelectEvent = false

      this.currentFormId = id
      this.currentForm = type + "-form"
    },
    showSelectEventForm(clearDate = false) {
      if (clearDate) {
        this.currentStartDate = null
        this.currentStartTime = null
        this.currentEndDate = null
        this.currentEndTime = null
      }

      this.showDialogSelectEvent = true
    },
    return_modal_form(data) {
      this.return_modal_cancel_form()

      if (data.mode == "add") {
        this.addEvent(data.data, data.module)
      }

      if (data.mode == "edit") {
        this.updateEvent(data.data, data.module)
      }
    },
    return_modal_cancel_form() {
      this.currentFormId = ""
      this.currentForm = ""
      this.currentStartDate = null
      this.currentStartTime = null
      this.currentEndDate = null
      this.currentEndTime = null
      this.currentCourseTypeId = null
      this.currentCourseTemplateId = null
      this.showDialogForm = false
    },
    showView(id, type) {
      this.currentViewId = id
      this.currentView = type + "-view"
    },
    return_modal_view(data) {
      this.return_modal_cancel_view()
    },
    return_modal_cancel_view() {
      this.currentViewId = ""
      this.currentView = ""
      this.showDialogView = false
    },
    handleEventClick(arg) {
      let id = arg.event.extendedProps.id
      let type = arg.event.extendedProps.type

      this.showForm(id, type)
    },
    handleDatesSelected(arg, arg2, arg3) {
      let view = arg.view.type
      let start = this.moment(arg.start)
      let end = this.moment(arg.end)

      if (view == "dayGridMonth" || view == "dayGridWeek") {
        this.currentStartTime = "08:00:00"
        this.currentEndTime = "08:00:00"
        end = this.moment(arg.end).subtract(1, "days")
      } else {
        this.currentStartTime = start.format(this.MYSQL_TIME_FORMAT)
        this.currentEndTime = end.format(this.MYSQL_TIME_FORMAT)
      }

      this.currentStartDate = start.format(this.MYSQL_DATE_FORMAT)
      this.currentEndDate = end.format(this.MYSQL_DATE_FORMAT)

      this.showSelectEventForm()
    },
    setupDraggable() {
      new Draggable(document.getElementById("draggable-items"), {
        itemSelector: ".fc-event-ct",
        eventData: function (eventEl) {
          let event = {
            id: eventEl.id,
            title: eventEl.innerText
          }
          return event
        }
      })
    },
    handleEventReceive(el) {
      let ids = el.event.id.split(",")

      if (ids.length == 2) {
        let start = this.moment(el.event.start)

        this.currentCourseTypeId = ids[0]
        this.currentCourseTemplateId = ids[1]
        this.currentStartDate = start.format(this.MYSQL_DATE_FORMAT)

        this.showForm(null, "course")
      }

      return false
    }
  }
}
</script>

<style scoped>
.form-modal {
  background: white;
}
</style>